import { Link2Off, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * CancelOrderPayByLinkDialog displays a confirmation modal to cancel an active
 * Pay By Link payment request for a take-order.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {() => void} props.onConfirm
 * @param {boolean} props.isLoading
 * @param {object} props.orderData - Active order data object
 * @param {number|string} [props.paymentAmount] - Specific amount to display (e.g. advance amount)
 */
export default function CancelOrderPayByLinkDialog({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  orderData,
  paymentAmount,
}) {
  const displayAmount = parseFloat(
    paymentAmount !== undefined && paymentAmount !== null
      ? paymentAmount
      : orderData?.pre_order?.advance_amount || orderData?.total_price || orderData?.order_total || 0
  ).toFixed(2);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[460px] bg-cblack4 border border-gray-700 text-white p-6 shadow-2xl rounded-xl animate-fadeIn">
        <DialogHeader className="flex flex-col justify-center items-center gap-3 text-center">
          {/* Warning Icon Badge */}
          <div className="relative flex justify-center items-center h-14 w-14 rounded-full bg-red-500/10 text-red-500 border border-red-500/20 shadow-inner">
            <Link2Off className="h-7 w-7" />
          </div>

          <DialogTitle className="text-white text-xl font-bold tracking-wide">
            Cancel Pay By Link
          </DialogTitle>

          <DialogDescription className="text-cwhite3 text-sm leading-relaxed max-w-[360px]">
            Are you sure you want to cancel the active Pay By Link payment request for{" "}
            <span className="text-cyellow font-semibold">Order #{orderData?.id || ""}</span>?
          </DialogDescription>
        </DialogHeader>

        {/* Summary Card */}
        <div className="my-4 space-y-3">
          <div className="bg-cblack3 p-4 rounded-xl border border-gray-700/80 flex justify-between items-center">
            <div className="flex flex-col text-left">
              <span className="text-xs text-cwhite3 uppercase tracking-wider font-semibold">
                Amount Due
              </span>
              <span className="text-sm text-cwhite2 mt-0.5">
                {orderData?.customer?.name || orderData?.customer?.full_name || orderData?.customer_full_name || "Customer"}
              </span>
            </div>
            <span className="text-cyellow text-2xl font-bold font-sans">
              £{displayAmount}
            </span>
          </div>

          <div className="bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs p-3 rounded-lg flex items-start gap-2.5">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-400" />
            <span>
              This will void the payment link request in the system. The customer will no longer be able to complete payment using this link.
            </span>
          </div>
        </div>

        <DialogFooter className="flex w-full justify-end gap-3 mt-2">
          <Button
            type="button"
            className="flex-1 bg-cblack7 text-white border border-gray-700 hover:bg-cblack6 transition"
            onClick={onClose}
            disabled={isLoading}
          >
            Keep Link
          </Button>

          <Button
            type="button"
            isLoading={isLoading}
            disabled={isLoading}
            className="flex-1 bg-cred hover:bg-cred/90 text-white font-bold transition"
            onClick={onConfirm}
          >
            Yes, Cancel Link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
